import { FC } from "react";
import { useSelector } from "react-redux";

import { PlanFeature } from "../../constants/plans";
import { isPlanFeatureEnabled } from "../../utils/isPlanFeatureEnabled";
import TopNav from "../../components/TopNav/TopNav";
import OverviewHeader from "../../components/OverviewHeader/OverviewHeader";
import Section from "../../components/Section/Section";
import { SourceTable } from "../../components/SourceTable";
import {
  MOCK_CHART_DATA,
  MOCK_CHART_SERIES,
  MOCK_TABLE_ROWS,
} from "../../components/OverviewSourceUsage/const";
import OverviewSourceUsageChart from "../../components/OverviewSourceUsage/OverviewSourceUsageChart/OverviewSourceUsageChart";
import OverviewSourceUsageStyles from "../../components/OverviewSourceUsage/OverviewSourceUsage.module.css";
import SearchExportToolbar from "../../components/SearchExportToolbar";
import SourceChartSkeleton from "./components/SourceChartSkeleton";
import SourceDomainsTableSkeleton from "./components/SourceDomainsTableSkeleton";
import { PlanFeatureBlurGate } from "../../components/PlanFeatureBlurGate";
import { TabSearchIcon, SourceUsageIcon } from "../../components/icons";
import { selectAuthSubscriptionPlan } from "../../store/selectors/auth";
import { useSourcePage } from "./hooks/useSourcePage";

import styles from "./Source.module.css";

const PAGE_SIZE = 20;
const MOCK_SOURCE_DATA = {
  chartData: MOCK_CHART_DATA,
  chartSeries: MOCK_CHART_SERIES,
  tableRows: MOCK_TABLE_ROWS,
};

const Source: FC = () => {
  const {
    dateFilter,
    setDateFilterFromUrl,
    lastUpdateTime,
    chart,
    showChartSkeleton,
    hasChartData,
    sourceDomainsPage,
    tableLoading,
    tableError,
    domainsPageRows,
    domainsPageTotal,
    searchInput,
    setSearchInput,
    handleDomainsPageChange,
    handleExportToExcel,
  } = useSourcePage();

  const subscriptionPlan = useSelector(selectAuthSubscriptionPlan);
  const isSourceLocked = !isPlanFeatureEnabled({feature: PlanFeature.Source, plan: subscriptionPlan});
  const hasChartContent = hasChartData && !!chart.data?.length && !!chart.series?.length;
  const hasTableContent = domainsPageTotal > 0 && domainsPageRows.length > 0;
  const shouldUseDemoData =
    isSourceLocked &&
    !showChartSkeleton &&
    !tableLoading &&
    !tableError &&
    !hasChartContent &&
    !hasTableContent;
  const sourceData = shouldUseDemoData
    ? MOCK_SOURCE_DATA
    : {
        chartData: chart.data,
        chartSeries: chart.series,
        tableRows: domainsPageRows,
      };
  const hasChartContentResolved = shouldUseDemoData || hasChartContent;
  const hasTableContentResolved = shouldUseDemoData || hasTableContent;

  const renderNoDataMessage = () => {
    return (
      <div className={OverviewSourceUsageStyles["overview_source_usage_empty"]}>
        <p className={OverviewSourceUsageStyles["overview_source_usage__empty"]}>No data for the selected period</p>
      </div>
    );
  };

  const renderErrorMessage = (error: string) => {
    return (
      <div className={OverviewSourceUsageStyles["overview_source_usage_empty"]}>
        <p className={OverviewSourceUsageStyles["overview_source_usage__empty"]}>{error}</p>
      </div>
    );
  };

  return (
    <>
      <TopNav title="Source" />
      <PlanFeatureBlurGate isLocked={isSourceLocked} showUpgradeButton>
        <div className={styles["source"]}>
          <OverviewHeader
            lastUpdateTime={lastUpdateTime}
            dateFilter={dateFilter}
            onDateFilterChange={setDateFilterFromUrl}
          />

          <Section icon={<SourceUsageIcon />} title="Source Usage">
              {showChartSkeleton ? (
                <SourceChartSkeleton />
              ) : !hasChartContentResolved ? (
                renderNoDataMessage()
              ) : (
                <OverviewSourceUsageChart
                  data={sourceData.chartData}
                  series={sourceData.chartSeries}
                  hideMoreSourcesLegend
                />
              )}
          </Section>

          <Section
            icon={<TabSearchIcon />}
            title="Domains"
            headerRight={
              <SearchExportToolbar
                searchValue={searchInput}
                onSearchChange={(e) => setSearchInput(e.target.value)}
                onExportClick={handleExportToExcel}
              />
            }
          >
              {tableLoading ? (
                <SourceDomainsTableSkeleton />
              ) : tableError ? (
                renderErrorMessage(tableError)
              ) : !hasTableContentResolved ? (
                renderNoDataMessage()
              ) : (
                <SourceTable
                  rows={sourceData.tableRows}
                  pageSize={PAGE_SIZE}
                  {...(!shouldUseDemoData && {
                    totalRows: domainsPageTotal,
                    currentPage: sourceDomainsPage.page,
                    onPageChange: handleDomainsPageChange,
                  })}
                />
              )}
          </Section>
        </div>
      </PlanFeatureBlurGate>
    </>
  );
};

export default Source;
